import React from 'react';
import { Button, Flex, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';
import type { ChartSeriesProvider } from '../parseChartResponseData';

export const ROLLING_AVERAGE_TOOLTIP =
  'Monitor 7-day averages, updated daily, to track trends and filter out short-term fluctuations.';

export const ALL_CRAWLERS_LABEL = 'All Crawlers';

export type CrawlerActivityLegendItem = {
  provider: ChartSeriesProvider;
  color: string;
};

type CrawlerActivityChartLegendProps = {
  items: CrawlerActivityLegendItem[];
  allCrawlersColor: string;
  itemsVisibility: boolean[];
  onItemClicked: (index: number) => void;
};

type LegendRowProps = {
  label: string;
  color: string;
  dashed?: boolean;
  visible: boolean;
  onClick: () => void;
};

const SWATCH_WIDTH = 20;
const SWATCH_HEIGHT = 8;
const SWATCH_DOT_RADIUS = 2.5;
const SWATCH_LINE_Y = SWATCH_HEIGHT / 2;

function LegendSwatch({ color, dashed }: { color: string; dashed?: boolean }) {
  return (
    <svg
      aria-hidden
      width={SWATCH_WIDTH}
      height={SWATCH_HEIGHT}
      style={{ flexShrink: 0, display: 'inline-block' }}
    >
      <line
        x1={0}
        x2={SWATCH_WIDTH}
        y1={SWATCH_LINE_Y}
        y2={SWATCH_LINE_Y}
        stroke={color}
        strokeWidth={1}
        strokeDasharray={dashed ? '2 2' : undefined}
      />
      <circle
        cx={SWATCH_WIDTH / 2}
        cy={SWATCH_LINE_Y}
        r={SWATCH_DOT_RADIUS}
        fill="#fff"
        stroke={color}
        strokeWidth={1}
      />
    </svg>
  );
}

function LegendRow({ label, color, dashed, visible, onClick }: LegendRowProps) {
  const { token } = theme.useToken();

  return (
    <Button
      type="text"
      block
      onClick={onClick}
      style={{
        height: 'auto',
        padding: `${token.paddingXXS}px ${token.paddingXS}px`,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: token.marginXS,
        textAlign: 'left',
      }}
    >
      <LegendSwatch color={color} dashed={dashed} />
      <Typography.Text
        style={{
          fontSize: token.fontSize,
          textDecoration: visible ? 'none' : 'line-through',
          color: visible ? token.colorText : token.colorTextTertiary,
        }}
      >
        {label}
      </Typography.Text>
    </Button>
  );
}

function CrawlerActivityChartLegend({
  items,
  allCrawlersColor,
  itemsVisibility,
  onItemClicked,
}: CrawlerActivityChartLegendProps) {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginXXS} style={{ height: '100%' }}>
      <Flex align="center" gap={token.marginXXS} style={{ marginBottom: token.marginXS }}>
        <Typography.Text
          strong
          type="secondary"
          style={{ fontSize: token.fontSize }}
        >
          7-day rolling average
        </Typography.Text>
        <CustomTooltip placement="top" message={ROLLING_AVERAGE_TOOLTIP}>
          <InfoCircleOutlined
            aria-label="rolling-average-info"
            style={{ color: token.colorTextSecondary }}
          />
        </CustomTooltip>
      </Flex>

      <LegendRow
        label={ALL_CRAWLERS_LABEL}
        color={allCrawlersColor}
        dashed
        visible={itemsVisibility[0] ?? true}
        onClick={() => onItemClicked(0)}
      />

      {items.map((item, i) => (
        <LegendRow
          key={item.provider}
          label={item.provider}
          color={item.color}
          visible={itemsVisibility[i + 1] ?? true}
          onClick={() => onItemClicked(i + 1)}
        />
      ))}
    </Flex>
  );
}

export default CrawlerActivityChartLegend;
